/* AUTO-GENERATED by gondola-filter-system/build.ts — DO NOT EDIT */

/**
 * Unified vehicle (rental car) filter and sort logic.
 *
 * Extracted from gondola-web-app VehicleFilterBar.tsx and VehicleCard.tsx.
 */

import type {
  VehicleFilterable,
  VehicleFilters,
  VehicleLocationDisplayable,
  VehiclePriceRange,
  VehicleSortOption,
  VehicleSortOrigin,
} from "./types.generated";

// ---------------------------------------------------------------------------
// Rate parsing
// ---------------------------------------------------------------------------

/**
 * Parse vehicle rate strings like "USD45.00" to a numeric value.
 * Returns Infinity for null/undefined/unparseable so they sort last.
 */
export function parseRateNumeric(rate: string | null | undefined): number {
  if (!rate) return Infinity;
  const match = rate.match(/^[A-Z]{3}([\d.]+)$/);
  return match ? parseFloat(match[1]) : Infinity;
}

// ---------------------------------------------------------------------------
// Price ranges (total-cost buckets)
// ---------------------------------------------------------------------------

/**
 * Linear-interpolated quantile of an ascending-sorted numeric array.
 * `p` is in [0, 1] (e.g. 0.25 for the first quartile).
 */
function quantile(sortedAsc: number[], p: number): number {
  if (sortedAsc.length === 0) return 0;
  if (sortedAsc.length === 1) return sortedAsc[0];
  const idx = (sortedAsc.length - 1) * p;
  const lo = Math.floor(idx);
  const hi = Math.ceil(idx);
  if (lo === hi) return sortedAsc[lo];
  return sortedAsc[lo] + (sortedAsc[hi] - sortedAsc[lo]) * (idx - lo);
}

/**
 * Pick a "nice" rounding step for bucket boundaries based on the overall
 * price scale, so labels read as round numbers ($25/$50/$100 increments)
 * rather than arbitrary cut points like $207.
 */
function niceStep(maxTotal: number): number {
  if (maxTotal < 200) return 10;
  if (maxTotal < 500) return 25;
  if (maxTotal < 1000) return 50;
  if (maxTotal < 5000) return 100;
  return 250;
}

function formatPrice(n: number): string {
  return `$${n.toLocaleString("en-US")}`;
}

function rangeLabel(min: number, max: number): string {
  if (min <= 0) return `Under ${formatPrice(max)}`;
  if (max === Infinity) return `${formatPrice(min)}+`;
  return `${formatPrice(min)} - ${formatPrice(max)}`;
}

/**
 * Stable identity for a price bucket: its dollar boundaries, NOT its position.
 *
 * Selections store this key (in `filters.selectedPriceRanges`) rather than an
 * array index, so a selection keeps meaning the exact same dollar range even
 * as the buckets are recomputed (results grow during polling, a new search
 * reuses prior filter state). `Infinity` is encoded literally so it round-trips.
 */
export function vehiclePriceRangeKey(range: VehiclePriceRange): string {
  return `${range.min}-${range.max === Infinity ? "Infinity" : range.max}`;
}

function parsePriceRangeKey(key: string): { min: number; max: number } | null {
  const dash = key.indexOf("-");
  if (dash < 0) return null;
  const min = Number(key.slice(0, dash));
  const maxStr = key.slice(dash + 1);
  const max = maxStr === "Infinity" ? Infinity : Number(maxStr);
  if (!Number.isFinite(min) || Number.isNaN(max)) return null;
  return { min, max };
}

/** Human label for a stored price-range key (e.g. for the active filter pill). */
export function vehiclePriceRangeKeyLabel(key: string): string {
  const parsed = parsePriceRangeKey(key);
  return parsed ? rangeLabel(parsed.min, parsed.max) : key;
}

/**
 * Whether a stored `selectedPriceRanges` entry is a usable total-cost key.
 * UI "active filter" checks (pills, counts, surfaced options) should use this
 * so they agree with `applyVehicleFilters`, which silently ignores any entry
 * that doesn't parse (e.g. a legacy bucket-index value like "0").
 */
export function isVehiclePriceRangeKey(key: string): boolean {
  return parsePriceRangeKey(key) !== null;
}

/**
 * Build up to four total-cost price buckets from a set of vehicles, using
 * quartile cut points rounded to a "nice" step.
 *
 * Call this with the FULL search result set (not a post-filter subset): the
 * returned boundaries are then stable as the user toggles other filters —
 * only a brand-new search recomputes them. Cars without a parseable
 * `totalRate` are ignored. Returns `[]` when there aren't enough distinct
 * prices to form a meaningful split (callers should hide the price filter).
 */
export function computeVehiclePriceRanges<T extends VehicleFilterable>(
  vehicles: T[]
): VehiclePriceRange[] {
  const totals = vehicles
    .map((v) => parseRateNumeric(v.totalRate))
    .filter((n) => Number.isFinite(n))
    .sort((a, b) => a - b);

  if (totals.length < 2 || totals[0] === totals[totals.length - 1]) return [];

  const step = niceStep(totals[totals.length - 1]);
  const roundTo = (n: number) => Math.round(n / step) * step;

  // Quartile cut points, rounded and de-duplicated. Collapsed cuts (e.g. a
  // tight price cluster) naturally yield fewer than four buckets.
  const cuts = Array.from(
    new Set([0.25, 0.5, 0.75].map((p) => roundTo(quantile(totals, p))))
  )
    .filter((c) => c > 0)
    .sort((a, b) => a - b);

  if (cuts.length === 0) return [];

  const ranges: VehiclePriceRange[] = [
    { label: rangeLabel(0, cuts[0]), min: 0, max: cuts[0] },
  ];
  for (let i = 1; i < cuts.length; i++) {
    ranges.push({
      label: rangeLabel(cuts[i - 1], cuts[i]),
      min: cuts[i - 1],
      max: cuts[i],
    });
  }
  ranges.push({
    label: rangeLabel(cuts[cuts.length - 1], Infinity),
    min: cuts[cuts.length - 1],
    max: Infinity,
  });

  return ranges;
}

// ---------------------------------------------------------------------------
// Filtering
// ---------------------------------------------------------------------------

/** Stable value used by both the pickup filter options and its predicate. */
export function vehiclePickupLocationValue(vehicle: VehicleFilterable): string {
  if (vehicle.deskKind === "city") {
    return (
      vehicle.deskAddress?.trim() ||
      vehicle.locationName?.trim() ||
      vehicle.pickupLocation?.trim() ||
      ""
    );
  }
  return vehicle.pickupLocation?.trim() || "";
}

/** Stable per-result drop-off value, with the search destination as fallback. */
export function vehicleDropoffLocationValue(
  vehicle: VehicleFilterable,
  searchDropoffLocation?: string | null
): string {
  return (
    vehicle.dropoffLogistics?.address?.trim() ||
    vehicle.dropoffLocation?.trim() ||
    searchDropoffLocation?.trim() ||
    ""
  );
}

function normalizedFilterValue(value: string | null | undefined): string {
  return value?.trim().toLocaleLowerCase() ?? "";
}

/**
 * Apply all vehicle filters. Returns a new filtered array.
 *
 * Price selections in `filters.selectedPriceRanges` are total-cost bucket
 * KEYS ("min-max", see `vehiclePriceRangeKey`), not array indices — so a
 * selection keeps meaning the exact same dollar range even as the buckets are
 * recomputed (results growing during polling, a new search reusing prior
 * filter state). Unparseable keys are ignored; if none remain, the price
 * filter is skipped rather than dropping every car.
 */
export function applyVehicleFilters<T extends VehicleFilterable>(
  vehicles: T[],
  filters: VehicleFilters
): T[] {
  const selectedPriceBuckets = (filters.selectedPriceRanges ?? [])
    .map(parsePriceRangeKey)
    .filter((range): range is { min: number; max: number } => range !== null);

  return vehicles.filter((v) => {
    // Price range filter (by total cost)
    if (selectedPriceBuckets.length > 0) {
      const rate = parseRateNumeric(v.totalRate);
      const inRange = selectedPriceBuckets.some(
        (range) => rate >= range.min && rate < range.max
      );
      if (!inRange) return false;
    }

    // Pickup location filter (airport code or distinct city-desk address)
    if (
      filters.selectedPickupLocations &&
      filters.selectedPickupLocations.length > 0 &&
      !filters.selectedPickupLocations.includes(vehiclePickupLocationValue(v))
    ) {
      return false;
    }

    // Drop-off location filter
    if (
      filters.selectedDropoffLocations &&
      filters.selectedDropoffLocations.length > 0 &&
      !filters.selectedDropoffLocations.includes(vehicleDropoffLocationValue(v))
    ) {
      return false;
    }

    // Car type filter
    if (
      filters.selectedCarTypes &&
      filters.selectedCarTypes.length > 0 &&
      !filters.selectedCarTypes.includes(v.vehicleCategory ?? "")
    ) {
      return false;
    }

    // Feature filter (AND logic: all selected features must be present)
    if (filters.selectedFeatures && filters.selectedFeatures.length > 0) {
      for (const feature of filters.selectedFeatures) {
        switch (feature) {
          case "Unlimited Mileage":
            if (!v.unlimitedMileage) return false;
            break;
          case "Air Conditioning":
            if (!v.airConditioning) return false;
            break;
          case "Automatic":
            if (v.transmissionType !== "Automatic") return false;
            break;
          case "Manual":
            if (v.transmissionType !== "Manual") return false;
            break;
        }
      }
    }

    // Vendor filter
    if (
      filters.selectedVendors &&
      filters.selectedVendors.length > 0 &&
      !filters.selectedVendors.some(
        (vendor) =>
          normalizedFilterValue(vendor) ===
          normalizedFilterValue(v.vendorName ?? v.vendorCode)
      )
    ) {
      return false;
    }

    // Payment type filter (OR logic: vehicle must accept at least one selected card type)
    if (
      filters.selectedPaymentTypes &&
      filters.selectedPaymentTypes.length > 0
    ) {
      const payments = v.acceptedPayments ?? [];
      const acceptsAny = filters.selectedPaymentTypes.some((code) =>
        payments.includes(code)
      );
      if (!acceptsAny) return false;
    }

    return true;
  });
}

// ---------------------------------------------------------------------------
// Sorting
// ---------------------------------------------------------------------------

/**
 * Sort vehicles by the given sort option. Returns a new sorted array.
 */
export function sortVehicles<T extends VehicleFilterable>(
  vehicles: T[],
  sortBy: VehicleSortOption,
  origin?: VehicleSortOrigin
): T[] {
  const sorted = [...vehicles];

  // Recommendation order is computed by the backend so web and mobile share the
  // same price, distance, loyalty, and applied-code ranking.
  switch (sortBy) {
    case "distance":
      if (origin) {
        sorted.sort(
          (a, b) =>
            vehicleDistanceMiles(a, origin) - vehicleDistanceMiles(b, origin)
        );
      }
      break;
    case "totalRate":
      sorted.sort(
        (a, b) => parseRateNumeric(a.totalRate) - parseRateNumeric(b.totalRate)
      );
      break;
    case "totalRateDesc":
      sorted.sort(
        (a, b) => parseRateNumeric(b.totalRate) - parseRateNumeric(a.totalRate)
      );
      break;
    case "vehicleClass":
      sorted.sort((a, b) =>
        (a.vehicleClass ?? "").localeCompare(b.vehicleClass ?? "")
      );
      break;
    case "recommendation":
    default:
      // Keep backend order
      break;
  }

  return sorted;
}

function vehicleDistanceMiles(
  vehicle: VehicleFilterable,
  origin: VehicleSortOrigin
): number {
  if (vehicle.pickupLatitude == null || vehicle.pickupLongitude == null) {
    return Infinity;
  }
  const radians = (degrees: number) => (degrees * Math.PI) / 180;
  const latDelta = radians(vehicle.pickupLatitude - origin.latitude);
  const lngDelta = radians(vehicle.pickupLongitude - origin.longitude);
  const startLat = radians(origin.latitude);
  const endLat = radians(vehicle.pickupLatitude);
  const haversine =
    Math.sin(latDelta / 2) ** 2 +
    Math.cos(startLat) * Math.cos(endLat) * Math.sin(lngDelta / 2) ** 2;
  return (
    3958.8 * 2 * Math.atan2(Math.sqrt(haversine), Math.sqrt(1 - haversine))
  );
}

// ---------------------------------------------------------------------------
// Location display
// ---------------------------------------------------------------------------

/**
 * 3-letter IATA airport code heuristic. The vehicle search UI always passes
 * a code here — empty/longer strings are treated as non-airport (city) pickups.
 */
export function isAirportCode(code: string | undefined | null): boolean {
  return !!code && /^[A-Z]{3}$/.test(code);
}

/**
 * Format a location code for display. Airport codes get an "Airport" suffix
 * (e.g. "LAX" → "LAX Airport"); non-airport codes pass through unchanged.
 * Returns null when the input is empty so callers can branch on it.
 */
export function formatVehicleLocationLabel(
  code: string | undefined | null
): string | null {
  if (!code) return null;
  return isAirportCode(code) ? `${code} Airport` : code;
}

/**
 * Whether a location's 3-letter code may be rendered as "{code} Airport".
 *
 * Travelport classifies each rental desk ("Airport", "CityCenterDowntown", "City",
 * "RailwayStation", ...). Only an airport desk is actually AT the airport — a
 * CityCenterDowntown desk still carries the metro's IATA code, so "BOS" for the Hertz
 * counter at 30 Park Plaza would read as "BOS Airport" if the code were trusted alone.
 *
 * A missing type means "unknown", which stays airport for backwards compatibility: rentals
 * booked before the type was persisted are overwhelmingly airport pickups, and their code is
 * the only location signal we have.
 */
export function shouldShowAirportLocationCode(
  locationType: string | undefined | null
): boolean {
  return !locationType || locationType.toLowerCase() === "airport";
}

/**
 * Resolve the one-line pickup/dropoff label a trips card shows for a rental.
 *
 * Airport desks render "{code} Airport" and deliberately drop the address — see
 * ``resolveLocationDisplay`` for why supplier address fields are unreliable there. Every
 * other desk type renders its street address, because that is the location the renter is
 * actually driving to, and falls back to the raw code only when no address was captured.
 */
export function resolveRentalLocationLabel(
  locationCode: string | undefined | null,
  locationType: string | undefined | null,
  address: string | undefined | null
): string | null {
  if (
    shouldShowAirportLocationCode(locationType) &&
    isAirportCode(locationCode)
  ) {
    return formatVehicleLocationLabel(locationCode);
  }
  return address || locationCode || null;
}

/**
 * Build pickup + dropoff display strings for a vehicle search-result card.
 *
 * **Airport pickups (3-letter IATA code, the vast majority):** return
 * ``"{Code} Airport"`` and deliberately discard ``pickupLogistics.address``
 * and ``locationName``. Across suppliers those two fields are unreliable —
 * Travelport returns counter descriptions in the Address column for some
 * vendors, and ``locationName`` is often a marketing string like
 * ``"OFFSITE - SHUTTLE TO COUNTER AND CAR"``. The detailed address surfaces
 * later on the booking flow / confirmation screens where there's room to
 * render it properly.
 *
 * **Non-airport pickups (city search):** keep the legacy
 * ``logistics.address → location_name → code`` fallback chain. City searches
 * surface a real address or place name reliably enough to render.
 *
 * ``sameLocation=true`` lets callers collapse the two rows into one with a
 * "Return to same location" sublabel. The ``*IsAirport`` flags drive icon
 * choice (plane vs. map-pin) without re-deriving from the code in the view.
 */
export function resolveLocationDisplay(
  car: VehicleLocationDisplayable,
  searchDropoffLocation?: string
): {
  pickup: string | null;
  dropoff: string | null;
  sameLocation: boolean;
  pickupIsAirport: boolean;
  dropoffIsAirport: boolean;
} {
  const pickupCode = car.pickupLocation;
  const dropoffCode = searchDropoffLocation ?? pickupCode;
  const sameLocation = !dropoffCode || dropoffCode === pickupCode;
  const pickupIsAirport = isAirportCode(pickupCode);
  const dropoffIsAirport = isAirportCode(dropoffCode);

  const pickup = pickupIsAirport
    ? `${pickupCode} Airport`
    : car.pickupLogistics?.address || car.locationName || pickupCode || null;

  let dropoff: string | null;
  if (sameLocation) {
    dropoff = pickup;
  } else if (dropoffIsAirport) {
    dropoff = `${dropoffCode} Airport`;
  } else {
    dropoff = car.dropoffLogistics?.address || dropoffCode || null;
  }

  return { pickup, dropoff, sameLocation, pickupIsAirport, dropoffIsAirport };
}
